import React, { useState } from "react";
import Header from "../../../../components/ui/Header";
import Footer from "../../../../components/ui/Footer";
import Button from "../../../../components/ui/Button";
import Icon from "../../../../components/AppIcon";

/* ===================== */
/* HELPER */
/* ===================== */
const renderTable = (title, rows) => (
  <div className="mb-10">
    <h3 className="font-bold text-lg mb-3">{title}</h3>
    <table className="w-full border border-gray-200 text-sm">
      <tbody>
        {rows.map(([label, value], i) => (
          <tr key={i} className="border-b">
            <td className="w-1/3 p-3 font-medium bg-gray-50">{label}</td>
            <td className="p-3">{value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const DescriptionRenderer = ({ sections }) => (
  <div className="space-y-6 text-[15px] leading-relaxed text-gray-700">
    {sections.map((item, i) => {
      if (item.type === "heading") {
        return (
          <h3
            key={i}
            className="text-lg font-semibold text-black mt-8"
          >
            {item.text}
          </h3>
        );
      }

      return <p key={i}>{item.text}</p>;
    })}
  </div>
);


/* ===================== */
/* TEMPLATE */
/* ===================== */
const MotorDetailTemplate = ({ data }) => {
  if (!data) return null;
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeTab, setActiveTab] = useState("deskripsi");

  const slides = data.hero?.slides || [];
  const activeSlide = slides[activeIndex];

  const activeVariant = data.hero?.variants?.find(
    (v) => v.key === activeSlide?.variantKey
  );

    const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
    const lowestPrice = data.hero?.variants?.reduce(
    (min, v) => (v.startPrice < min ? v.startPrice : min),
    data.hero?.variants?.[0]?.startPrice
  );

  const [activeVariantKey, setActiveVariantKey] = useState(null);


  return (
    <>
      <Header />

    <div className="leading-relaxed">

      <div className="max-w-7xl mx-auto px-4 py-8">

        {/* ================= HERO DETAIL (CLONE UI) ================= */}
        <div className="bg-white pt-1 pb-8 text-[18px] md:text-[17px]" data-aos="fade-up">

          {/* IMAGE + SPEC */}
          <div className="grid grid-cols-12 items-center gap-16 -mt-10">

            {/* LEFT IMAGE */}
            <div
              key={`image-${activeIndex}`}
              className="
                col-span-7 flex justify-center relative
                opacity-0 -translate-x-16
                animate-[fadeLeft_1.2s_ease-out_forwards]
              "
              >
              <img
                src={activeSlide?.image}
                alt={data.name}
                className="w-full max-w-[640px] object-contain transition-opacity duration-300"
              />
              <button
                onClick={() =>
                  setActiveIndex((prev) =>
                    prev === 0 ? slides.length - 1 : prev - 1
                  )
                }
                className="absolute left-0 top-1/2 -translate-y-1/2 bg-white shadow rounded-full w-10 h-10"
              >
                ‹
              </button>

              <button
                onClick={() =>
                  setActiveIndex((prev) =>
                    prev === slides.length - 1 ? 0 : prev + 1
                  )
                }
                className="absolute right-0 top-1/2 -translate-y-1/2 bg-white shadow rounded-full w-10 h-10"
              >
                ›
              </button>

            </div>

            {/* RIGHT CARD */}
            <div
              key={`spec-${activeIndex}`}
              className="
                col-span-5
                opacity-0 translate-x-16
                animate-[fadeRight_1.2s_ease-out_forwards]
              "
            >

              <div className="border border-gray-200 rounded-xl p-8 bg-white">

                <h2 className="text-2xl font-bold mb-2">{data.name}</h2>
                <p className="text-sm text-gray-500 mb-6">
                  {data.hero?.shortDescription}
                </p>

                {/* SPEC CARD */}
                <div className="border border-gray-200 rounded-lg divide-y mb-8">

                  <SpecRow label="ENGINE" value={activeSlide?.specs?.engine} />
                  <SpecRow label="POWER" value={activeSlide?.specs?.power} />

                  {activeSlide?.specs?.smartKey && (
                    <SpecRow label="SMART KEY" value="Available" />
                  )}

                  {activeVariant?.startPrice && (
                    <SpecRow
                      label="HARGA MULAI DARI"
                      value={`Rp ${activeVariant.startPrice.toLocaleString("id-ID")}`}
                    />
                  )}

                </div>


                {/* BUTTONS */}
                <div className="flex gap-4">
                  <button
                    onClick={() =>
                      window.open(
                        `https://wa.me/6288293642972?text=Halo saya tertarik ${activeVariant.fullName} dengan harga mulai dari Rp ${activeVariant.startPrice.toLocaleString(
                          "id-ID"
                        )}`,
                        "_blank"
                      )
                    }
                    className="
                      flex-1 py-3 rounded-md
                      bg-red-500
                      border border-red-500
                      text-white
                      text-sm font-semibold
                      transition-all duration-300
                      hover:bg-white hover:text-red-500
                    "
                  >
                    CONTACT DEALER
                  </button>

                  <a
                    href="/credit-simulation"
                    className="
                      flex-1 py-3 rounded-md
                      bg-white
                      border border-red-500
                      text-red-500
                      text-center text-sm font-semibold
                      transition-all duration-300
                      hover:bg-red-500 hover:text-white
                    "
                  >
                    CREDIT SIMULATION
                  </a>


                </div>

              </div>
            </div>

          </div>
        </div>

       {/* ================= VARIAN MOTOR ================= */}
      <div className="pt-12 pb-20 bg-gray-50" data-aos="fade-up">
        <div className="max-w-7xl mx-auto px-4">

          <h2 className="text-3xl font-bold text-center mb-16">
            Varian Motor
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-14">

            {data.hero.variants.map((variant) => {
            const slide = data.hero.slides.find(
              (s) => s.variantKey === variant.key
            );

            if (!slide) return null;

            const isActive = activeVariantKey === variant.key;

            return (
              <div
                key={variant.key}
                className="text-center group cursor-pointer"
                onMouseEnter={() => {
                  if (!isMobile) setActiveVariantKey(variant.key);
                }}
                onMouseLeave={() => {
                  if (!isMobile) setActiveVariantKey(null);
                }}
                onClick={() => {
                  if (isMobile) {
                    setActiveVariantKey(
                      isActive ? null : variant.key
                    );
                  }
                }}
              >
                {/* IMAGE */}
                <div className="relative mb-6">
                  <img
                    src={slide.image}
                    alt={variant.fullName}
                    className={`
                      mx-auto max-h-[260px] object-contain
                      transition-all duration-500 ease-out
                      ${isActive ? "scale-105 -translate-y-2" : "scale-100 translate-y-0"}
                    `}
                  />

                  {/* OVERLAY */}
                  <div
                    className={`
                      absolute inset-0 flex items-center justify-center
                      bg-black/60 rounded-lg
                      transition-all duration-300
                      ${isActive ? "opacity-100" : "opacity-0 pointer-events-none"}
                    `}
                  >
                    <div className="flex flex-col gap-3">
                      
                      {/* BELI MOTOR */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation(); // ⬅️ WAJIB (mobile aman)
                          window.open(
                            `https://wa.me/6288293642972?text=${encodeURIComponent(
                              `Halo, saya tertarik membeli ${variant.fullName} dengan harga mulai dari Rp ${variant.startPrice.toLocaleString("id-ID")}`
                            )}`,
                            "_blank"
                          );
                        }}
                        className="
                          px-8 py-3
                          bg-red-600 text-white font-semibold
                          rounded-md
                          hover:bg-red-700
                          hover:scale-105
                          transition-all duration-300
                        "
                      >
                        Beli Motor
                      </button>

                      {/* SIMULASI KREDIT */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation(); // ⬅️ WAJIB
                          window.location.href = "/credit-simulation";
                        }}
                        className="
                          px-8 py-3
                          bg-white text-red-600 font-semibold
                          rounded-md
                          hover:bg-gray-100
                          hover:scale-105
                          transition-all duration-300
                        "
                      >
                        Simulasi Kredit
                      </button>

                    </div>
                  </div>

                </div>

                {/* TITLE */}
                <p className="font-semibold text-lg mb-1 tracking-tight">
                  {variant.fullName}
                </p>

                <p className="text-blue-700 font-bold text-xl">
                  Rp {variant.startPrice.toLocaleString("id-ID")}
                </p>
              </div>
            );
          })}

          </div>
        </div>
      </div>

      {/* ================= PRODUCT INFO TABS ================= */}
      <div className="py-20" data-aos="fade-up">
        <div className="max-w-7xl mx-auto px-4">

          {/* TAB HEADER */}
          <div className="flex gap-8 border-b mb-10 text-sm font-semibold">
            {[
              { key: "deskripsi", label: "Deskripsi" },
              { key: "spesifikasi", label: "Spesifikasi" },
              { key: "fitur", label: "Fitur Canggih" },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`pb-3 transition ${
                  activeTab === tab.key
                    ? "text-red-600 border-b-2 border-red-600"
                    : "text-gray-500 hover:text-black"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* ================= DESKRIPSI ================= */}
          {activeTab === "deskripsi" && (
            <div className="space-y-5 text-[15px] leading-relaxed">
              <DescriptionRenderer sections={data.description.sections} />

              {data.longDescription?.quote && (
                <blockquote className="mt-6 italic border-l-4 pl-4 border-red-600">
                  “{data.longDescription.quote}”
                </blockquote>
              )}
            </div>
          )}


          {/* ================= SPESIFIKASI ================= */}
          {activeTab === "spesifikasi" && (
            <div className="mt-6">
              {renderTable("Mesin", data.specificationsDetail.mesin)}
              {renderTable("Rangka & Kaki-Kaki", data.specificationsDetail.rangka)}
              {renderTable("Dimensi & Berat", data.specificationsDetail.dimensi)}
              {renderTable("Kapasitas", data.specificationsDetail.kapasitas)}
              {renderTable("Kelistrikan", data.specificationsDetail.kelistrikan)}
            </div>
          )}

          {/* ================= FITUR CANGGIH ================= */}
          {activeTab === "fitur" && (
            <div className="space-y-8">
              <h3 className="text-xl font-bold">
                {data.fullFeatures.title}
              </h3>

              <ul className="space-y-6">
                {data.fullFeatures.items.map((item, i) => (
                  <li key={i} className="flex gap-4">
                    <span className="mt-2 w-2 h-2 bg-black rounded-full" />
                    <div>
                      <p className="font-semibold">{item.title}</p>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

        </div>
      </div>
       
      </div>

    </div>

    {/* ================= CTA MOTOR ================= */}
    <section className="py-16 md:py-20 bg-red-600 text-white">
      <div className="max-w-7xl mx-auto px-4">
        <div
          className="max-w-4xl mx-auto text-center space-y-6"
          data-aos="fade-up"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/10 mb-4 mx-auto">
            <Icon name="MessageCircle" size={32} color="white" strokeWidth={2} />
          </div>

          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold">
            Tertarik dengan {data.name}?
          </h2>

          <p className="text-base md:text-lg text-white/90 max-w-2xl mx-auto">
            Dapatkan informasi harga, promo terbaru, dan simulasi kredit
            {` ${data.name}`} dengan harga mulai dari{" "}
            <strong>
              Rp {lowestPrice?.toLocaleString("id-ID")}
            </strong>.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">

            {/* CHAT WHATSAPP */}
            <Button
              variant="default"
              size="lg"
              iconName="MessageCircle"
              iconPosition="left"
              onClick={() => {
                const message = `Halo, saya tertarik membeli ${data.name}
    Harga mulai dari Rp ${lowestPrice?.toLocaleString("id-ID")}
    Mohon info promo & simulasi kreditnya.`;

                window.open(
                  `https://wa.me/6288293642972?text=${encodeURIComponent(message)}`,
                  "_blank"
                );
              }}
              className="bg-white text-red-600 hover:bg-white/90"
            >
              Chat WhatsApp
            </Button>

            {/* SIMULASI KREDIT */}
            <Button
              variant="outline"
              size="lg"
              iconName="Calculator"
              iconPosition="left"
              onClick={() => window.location.href = "/credit-simulation"}
              className="border-white text-white hover:bg-white hover:text-red-600"
            >
              Simulasi Kredit
            </Button>

          </div>
        </div>
      </div>
    </section>
    
      <Footer />
    </>
  );
};


const SpecRow = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="flex justify-between px-4 py-3 text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
};


export default MotorDetailTemplate;